// Add these two things to your existing CreateUserForm.jsx:
//
// 1. Import BulkUserUpload at the top:
//    import BulkUserUpload from './BulkUserUpload';
//
// 2. Add state for modal:
//    const [showBulkUpload, setShowBulkUpload] = useState(false);
//
// 3. Replace the hero header section with the version below (adds the "Bulk Import" button)
// 4. Add the BulkUserUpload modal just before the closing </div> of the component return.
//

import React, { useContext, useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ApiURL from '../Api/Api';
import Navbar from './Navbar';
import Sidebar from './Sidebar';
import BulkUserUpload from './Bulkuserupload';  // ← ADD THIS IMPORT
import { SidebarContext } from './SidebarContext';
import {
  UserPlusIcon,
  UserGroupIcon,  // ← ADD THIS
  ShieldCheckIcon,
  WrenchScrewdriverIcon,
  BuildingOffice2Icon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  EyeIcon,
  EyeSlashIcon,
  XMarkIcon,
  UsersIcon,
  ChartBarIcon,
  CalendarDaysIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';

/* ── helpers ─────────────────────────────────────────────────── */
const CardAccent = ({ color = 'bg-brand-600' }) => (
  <div className={`h-0.5 w-full ${color}`} />
);

const FieldLabel = ({ children, required }) => (
  <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1.5">
    {children}{required && <span className="text-rose-500 ml-0.5">*</span>}
  </label>
);

const inputCls = 'w-full h-10 px-3 text-[13px] font-medium text-slate-700 bg-white border border-slate-200 rounded-lg outline-none transition-all focus:border-brand-500 focus:ring-2 focus:ring-brand-100 placeholder:text-slate-300';
const selectCls = inputCls + ' appearance-none cursor-pointer';

/* ── Plan usage bar ──────────────────────────────────────────── */
const UsageBar = ({ label, used, max, color }) => {
  const pct = max > 0 ? Math.round((used / max) * 100) : 0;
  const isHigh = pct >= 80;
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[11px] text-slate-500 font-medium">{label}</span>
        <span className="text-[11px] font-bold text-slate-700 tabular-nums">{used}<span className="text-slate-400 font-normal">/{max}</span></span>
      </div>
      <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-700 ${isHigh ? 'bg-rose-400' : color}`}
          style={{ width: `${Math.min(pct, 100)}%` }}
        />
      </div>
    </div>
  );
};

/* ── Remaining slot badge ────────────────────────────────────── */
const SlotBadge = ({ label, value, icon: Icon, color }) => (
  <div className="bg-white rounded-lg border border-slate-200 p-3 flex flex-col gap-1.5 shadow-sm">
    <div className="flex items-center gap-1.5">
      <Icon className={`w-3.5 h-3.5 ${color}`} />
      <span className="text-[10px] text-slate-500 font-bold uppercase tracking-wider">{label}</span>
    </div>
    <p className={`text-2xl font-bold ${value === 0 ? 'text-rose-600' : 'text-slate-900'}`}>{value}</p>
    <p className="text-[9px] text-slate-400 uppercase tracking-wider font-bold">slots left</p>
  </div>
);

/* ── Site chip ───────────────────────────────────────────────── */
const SiteChip = ({ site, selected, onClick }) => (
  <button type="button" onClick={onClick}
    className={`flex items-center gap-1.5 px-3 py-2 rounded-xl border text-[12px] font-semibold transition-all touch-manipulation ${
      selected
        ? 'bg-brand-600 border-brand-600 text-white shadow-sm'
        : 'bg-white border-slate-200 text-slate-600 hover:border-brand-300 hover:bg-brand-50'
    }`}>
    <BuildingOffice2Icon className="w-3.5 h-3.5 flex-shrink-0" />
    {site.site_name}
  </button>
);

/* ── Role option card ────────────────────────────────────────── */
const RoleCard = ({ role, selected, onClick, disabled }) => {
  const map = {
    admin:  { label: 'Admin',  sub: 'Manages workers & transactions', icon: ShieldCheckIcon, color: 'text-brand-600', bg: 'bg-brand-600' },
    worker: { label: 'Worker', sub: 'Field-level transaction entry',   icon: WrenchScrewdriverIcon, color: 'text-emerald-600', bg: 'bg-emerald-600' },
  };
  const { label, sub, icon: Icon, color, bg } = map[role] || {};
  return (
    <button type="button" onClick={!disabled ? onClick : undefined}
      className={`relative flex items-start gap-3 p-3.5 rounded-xl border-2 transition-all text-left touch-manipulation w-full ${
        selected ? `border-brand-500 bg-brand-50` : 'border-slate-200 bg-white hover:border-brand-200 hover:bg-brand-50/40'
      } ${disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}>
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${selected ? bg : 'bg-slate-100'}`}>
        <Icon className={`w-4 h-4 ${selected ? 'text-white' : color}`} />
      </div>
      <div className="min-w-0">
        <p className={`text-[13px] font-bold ${selected ? 'text-brand-900' : 'text-slate-800'}`}>{label}</p>
        <p className="text-[10px] text-slate-500 mt-0.5">{sub}</p>
      </div>
      {selected && (
        <div className="absolute top-2 right-2 w-4 h-4 bg-brand-600 rounded-full flex items-center justify-center">
          <CheckCircleIcon className="w-3 h-3 text-white" />
        </div>
      )}
    </button>
  );
};

/* ══════════════════════════════════════════════════════════════ */
const CreateUserForm = () => {
  const { isSidebarOpen } = useContext(SidebarContext);

  const companyId   = localStorage.getItem('company_id') || '';
  const creatorId   = localStorage.getItem('id') || '';
  const creatorRole = localStorage.getItem('role') || '';
  const createdBy   = localStorage.getItem('user_name') || '';

  const [formData, setFormData] = useState({
    user_name: '', password: '', role: '', email: '', phone: '', assigned_sites: [],
  });
  const [siteOptions, setSiteOptions]   = useState([]);
  const [planInfo, setPlanInfo]         = useState(null);
  const [usageInfo, setUsageInfo]       = useState(null);
  const [success, setSuccess]           = useState('');
  const [error, setError]               = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPass, setShowPass]         = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showBulkUpload, setShowBulkUpload] = useState(false); // ← ADD THIS STATE

  const availableRoles = useMemo(() =>
    creatorRole === 'admin' ? ['worker'] : ['admin', 'worker'],
    [creatorRole]
  );

  const remaining = useMemo(() => {
    if (!planInfo || !usageInfo) return null;
    return {
      users:   Math.max((Number(planInfo.max_users)   || 0) - (Number(usageInfo.total_users)  || 0), 0),
      admins:  Math.max((Number(planInfo.max_admins)  || 0) - (Number(usageInfo.admin_count)  || 0), 0),
      workers: Math.max((Number(planInfo.max_workers) || 0) - (Number(usageInfo.worker_count) || 0), 0),
    };
  }, [planInfo, usageInfo]);

  const loadData = async () => {
    if (!companyId) return;
    try {
      const [sitesRes, usageRes] = await Promise.all([
        fetch(`${ApiURL}/getSites.php?company_id=${companyId}&user_id=${creatorId}`),
        fetch(`${ApiURL}/getCompanyPackageUsage.php?company_id=${companyId}`),
      ]);
      const sitesData = await sitesRes.json();
      const usageData = await usageRes.json();
      setSiteOptions(sitesData.sites || []);
      if (usageData.status === 'success') {
        setPlanInfo(usageData.plan || null);
        setUsageInfo(usageData.usage || null);
      }
    } catch (e) { console.error(e); }
  };

  useEffect(() => { loadData(); }, [companyId, creatorId]);

  const refreshUsage = async () => {
    setIsRefreshing(true);
    await loadData();
    setIsRefreshing(false);
  };

  const toggleSite = (siteName) => {
    setFormData(p => ({
      ...p,
      assigned_sites: p.assigned_sites.includes(siteName)
        ? p.assigned_sites.filter(s => s !== siteName)
        : [...p.assigned_sites, siteName],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccess(''); setError(''); setIsSubmitting(true);
    try {
      const res    = await fetch(`${ApiURL}/create_user.php`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, created_by: createdBy, company_id: companyId, creator_id: creatorId, creator_role: creatorRole }),
      });
      const result = await res.json();
      if (result.status !== 'success') throw new Error(result.message || 'Failed to create user');
      setSuccess(`${formData.role === 'admin' ? 'Admin' : 'Worker'} "${formData.user_name}" created successfully!`);
      setFormData({ user_name: '', password: '', role: '', email: '', phone: '', assigned_sites: [] });
      await refreshUsage();
    } catch (e) { setError(e.message || 'Error creating user'); }
    finally { setIsSubmitting(false); }
  };

  return (
    <div className="app-shell min-h-screen">
      <Navbar />
      <div className="flex pt-16">
        <Sidebar />
        <motion.main
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}
          className={`flex-1 min-h-screen transition-all duration-300 min-w-0 ${isSidebarOpen ? 'lg:ml-72' : ''}`}
        >
          <div className="max-w-screen-2xl mx-auto px-3 py-4 sm:px-5 sm:py-5 lg:px-6 lg:py-6 pb-10">

            {/* ── Hero header ── REPLACED WITH VERSION THAT HAS BULK IMPORT BUTTON ── */}
            <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
              className="page-surface overflow-hidden mb-4">
              <CardAccent />
              <div className="px-4 py-4 sm:px-6 sm:py-5">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-brand-600 mb-1">User Management</p>
                    <h1 className="text-lg sm:text-2xl font-bold text-slate-900 leading-tight">Create User</h1>
                    <p className="text-[11px] text-slate-500 mt-1">
                      {creatorRole === 'company_owner' ? 'Create admins and workers within your package limits' : 'Create workers for your team'}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      type="button"
                      onClick={() => setShowBulkUpload(true)}
                      className="pro-button pro-button-secondary h-9 px-3 text-[11px]"
                    >
                      <UserGroupIcon className="w-4 h-4" />
                      <span className="hidden sm:inline">Bulk Import</span>
                    </button>
                    <div className="w-12 h-12 rounded-xl bg-brand-100 flex items-center justify-center">
                      <UserPlusIcon className="w-6 h-6 text-brand-700" />
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* ── Two-column grid ──────────────────────────────── */}
            <div className="grid grid-cols-1 xl:grid-cols-[0.9fr_1.1fr] gap-4">

              {/* ─── LEFT: Plan info panel ───────────────────── */}
              <motion.div initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.05 }}
                className="pro-card overflow-hidden flex flex-col">
                <CardAccent />
                <div className="px-5 py-5 flex-1 flex flex-col">

                  {/* Plan name + refresh */}
                  <div className="flex items-start justify-between gap-3 mb-4">
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Active Plan</p>
                      <h2 className="text-lg font-bold text-slate-900 leading-tight">
                        {planInfo?.package_name || <span className="text-slate-500 italic text-base">No package linked</span>}
                      </h2>
                    </div>
                    <button onClick={refreshUsage}
                      className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center text-slate-500 hover:text-brand-700 transition-colors flex-shrink-0 mt-1">
                      <ArrowPathIcon className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                    </button>
                  </div>

                  {planInfo && usageInfo && remaining ? (
                    <>
                      {/* Slot badges */}
                      <div className="grid grid-cols-3 gap-2 mb-5">
                        <SlotBadge label="Total"   value={remaining.users}   icon={UsersIcon}           color="text-brand-600"    />
                        <SlotBadge label="Admins"  value={remaining.admins}  icon={ShieldCheckIcon}     color="text-amber-600"  />
                        <SlotBadge label="Workers" value={remaining.workers} icon={WrenchScrewdriverIcon} color="text-emerald-600" />
                      </div>

                      {/* Usage bars */}
                      <div className="space-y-3 mb-5">
                        <UsageBar label="Total Users"    used={usageInfo.total_users}  max={planInfo.max_users}   color="bg-brand-600"    />
                        <UsageBar label="Admins"         used={usageInfo.admin_count}  max={planInfo.max_admins}  color="bg-amber-500"  />
                        <UsageBar label="Workers"        used={usageInfo.worker_count} max={planInfo.max_workers} color="bg-emerald-500" />
                      </div>

                      {/* Subscription end */}
                      <div className="mt-auto flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5">
                        <CalendarDaysIcon className="w-4 h-4 text-brand-600 flex-shrink-0" />
                        <div>
                          <p className="text-[9px] text-slate-500 uppercase tracking-wider font-bold">Valid Until</p>
                          <p className="text-[12px] text-slate-700 font-semibold mt-0.5">{planInfo.subscription_end_date || '-'}</p>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="flex-1 flex flex-col items-center justify-center py-10 gap-3">
                      <ChartBarIcon className="w-10 h-10 text-slate-300" />
                      <p className="text-sm text-slate-500 text-center">Plan details unavailable</p>
                    </div>
                  )}
                </div>
              </motion.div>

              {/* ─── RIGHT: Create user form ──────────────────── */}
              <motion.div initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 }}
                className="pro-card overflow-hidden">
                <CardAccent />
                <div className="px-5 py-5">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1">New User</p>
                  <h2 className="text-[15px] font-bold text-slate-800 mb-4">Fill in user details</h2>

                  {/* Alerts */}
                  <AnimatePresence>
                    {success && (
                      <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                        className="flex items-start gap-2.5 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 mb-4">
                        <CheckCircleIcon className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                        <p className="text-[12px] text-emerald-700 font-semibold">{success}</p>
                        <button onClick={() => setSuccess('')} className="ml-auto"><XMarkIcon className="w-3.5 h-3.5 text-emerald-400" /></button>
                      </motion.div>
                    )}
                    {error && (
                      <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                        className="flex items-start gap-2.5 bg-rose-50 border border-rose-200 rounded-xl px-4 py-3 mb-4">
                        <ExclamationCircleIcon className="w-4 h-4 text-rose-500 flex-shrink-0 mt-0.5" />
                        <p className="text-[12px] text-rose-700 font-semibold">{error}</p>
                        <button onClick={() => setError('')} className="ml-auto"><XMarkIcon className="w-3.5 h-3.5 text-rose-400" /></button>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <form onSubmit={handleSubmit} className="space-y-4">

                    {/* Row 1: Username + Password */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <FieldLabel required>Username</FieldLabel>
                        <input type="text" value={formData.user_name} required
                          onChange={e => setFormData(p => ({ ...p, user_name: e.target.value }))}
                          placeholder="e.g. john_worker" className={inputCls} />
                      </div>
                      <div>
                        <FieldLabel required>Password</FieldLabel>
                        <div className="relative">
                          <input type={showPass ? 'text' : 'password'} value={formData.password} required
                            onChange={e => setFormData(p => ({ ...p, password: e.target.value }))}
                            placeholder="Min 6 characters" className={inputCls + ' pr-10'} />
                          <button type="button" onClick={() => setShowPass(p => !p)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-300 hover:text-slate-500 transition-colors">
                            {showPass ? <EyeSlashIcon className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Row 2: Email + Phone */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <FieldLabel>Email</FieldLabel>
                        <input type="email" value={formData.email}
                          onChange={e => setFormData(p => ({ ...p, email: e.target.value }))}
                          placeholder="user@company.com" className={inputCls} />
                      </div>
                      <div>
                        <FieldLabel>Phone</FieldLabel>
                        <input type="text" value={formData.phone}
                          onChange={e => setFormData(p => ({ ...p, phone: e.target.value }))}
                          placeholder="+91 9876543210" className={inputCls} />
                      </div>
                    </div>

                    {/* Role selector */}
                    <div>
                      <FieldLabel required>Role</FieldLabel>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {['admin', 'worker'].map(r => (
                          <RoleCard key={r} role={r}
                            selected={formData.role === r}
                            disabled={!availableRoles.includes(r)}
                            onClick={() => setFormData(p => ({ ...p, role: r }))}
                          />
                        ))}
                      </div>
                    </div>

                    {/* Site assignment */}
                    {siteOptions.length > 0 && (
                      <div>
                        <div className="flex items-center justify-between mb-1.5">
                          <FieldLabel>Assign Sites</FieldLabel>
                          {formData.assigned_sites.length > 0 && (
                            <button type="button" onClick={() => setFormData(p => ({ ...p, assigned_sites: [] }))}
                              className="text-[10px] text-rose-400 font-bold hover:text-rose-500 transition-colors">
                              Clear all
                            </button>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {siteOptions.map(site => (
                            <SiteChip key={site.site_id} site={site}
                              selected={formData.assigned_sites.includes(site.site_name)}
                              onClick={() => toggleSite(site.site_name)} />
                          ))}
                        </div>
                        {formData.assigned_sites.length > 0 && (
                          <p className="text-[10px] text-slate-400 mt-2">
                            {formData.assigned_sites.length} site{formData.assigned_sites.length > 1 ? 's' : ''} selected:
                            <span className="font-semibold text-slate-600 ml-1">{formData.assigned_sites.join(', ')}</span>
                          </p>
                        )}
                      </div>
                    )}

                    {/* Submit */}
                    <div className="pt-2 border-t border-slate-100">
                      <motion.button type="submit" disabled={isSubmitting || !formData.role}
                        whileTap={{ scale: 0.98 }}
                        className="pro-button pro-button-primary w-full h-11 text-[13px] disabled:opacity-50 disabled:cursor-not-allowed">
                        {isSubmitting ? (
                          <><ArrowPathIcon className="w-4 h-4 animate-spin" /> Creating User...</>
                        ) : (
                          <><UserPlusIcon className="w-4 h-4" />
                          Create {formData.role === 'admin' ? 'Admin' : formData.role === 'worker' ? 'Worker' : 'User'}
                          </>
                        )}
                      </motion.button>
                      {!formData.role && (
                        <p className="text-[10px] text-slate-400 text-center mt-2">Select a role to continue</p>
                      )}
                    </div>
                  </form>
                </div>
              </motion.div>

            </div>
          </div>
        </motion.main>
      </div>

      {/* ── ADD BULK USER UPLOAD MODAL HERE (before closing div) ── */}
      <AnimatePresence>
        {showBulkUpload && (
          <BulkUserUpload
            siteOptions={siteOptions}
            remaining={remaining}
            availableRoles={availableRoles}
            onClose={() => setShowBulkUpload(false)}
            onSuccess={(count) => {
              setShowBulkUpload(false);
              setSuccess(`${count} user${count > 1 ? 's' : ''} imported successfully!`);
              refreshUsage();
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default CreateUserForm;
